import { createContext, useContext, useEffect, useRef, useState } from "react";
import api, { apiError } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);      // null = checking, false = anon, object = authed
  const [loading, setLoading] = useState(true);
  const authed = useRef(false);

  useEffect(() => {
    api
      .get("/auth/me")
      .then((r) => { authed.current = true; setUser(r.data); })
      .catch(() => setUser(false))
      .finally(() => setLoading(false));
  }, []);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    authed.current = true;
    setUser(data.user);
    return data.user;
  };

  const refreshUser = async () => {
    if (!authed.current) return null;
    try { const { data } = await api.get("/auth/me"); setUser((u) => (JSON.stringify(u) === JSON.stringify(data) ? u : data)); return data; }
    catch (e) {
      if (e.response?.status === 401) { authed.current = false; window.location.href = "/login"; }
      return null;
    }
  };

  // Pick up role/permission changes (or a revoked session) when returning to the tab.
  useEffect(() => {
    const onFocus = () => { refreshUser(); };
    window.addEventListener("focus", onFocus);
    const t = setInterval(() => { if (document.visibilityState === "visible") refreshUser(); }, 60000);
    return () => { window.removeEventListener("focus", onFocus); clearInterval(t); };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch (e) { console.warn("Logout request failed", e); }
    authed.current = false;
    setUser(false);
    window.location.href = "/login";
  };

  const can = (perm) => {
    if (!user || !user.permissions) return false;
    return user.permissions.includes("*") || user.permissions.includes(perm);
  };

  const isSuperAdmin = !!(user && user.permissions && user.permissions.includes("*"));

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, can, isSuperAdmin, apiError, refreshUser }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
